import { Box, List, useMediaQuery, styled } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import WBCONST from '../shared/constants/wb.const';
import SidebarCollapsed from './component/SidebarCollapsed';
import SingleLevelItem from './component/SingleLevelItem';
import SideBarHoverExpanded from './component/SideBarHoverExpanded';
import { getUserRole } from '../shared/utils/utils';
import { getColors } from '../theme';

const colors = getColors();

const { SIDEBAR_WIDTH, SIDEBAR_WIDTH_MIN } = WBCONST;

const openedMixin = (theme) => ({
  width: SIDEBAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.ease,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: 'transparent',
  borderRight: 'none',
});

const closedMixin = (theme) => ({
  overflowX: 'hidden',
  borderRight: 'none',
  width: SIDEBAR_WIDTH_MIN,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: SIDEBAR_WIDTH,
  flexShrink: 0,
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

function AppSidebar({
  open,
  setOpen,
  toggleDrawer,
  drawopen,
  matchMenu,
  openSubMenuId,
  setOpenSubMenuId,
}) {
  const matchMobile = useMediaQuery('(max-width:800px)');
  const [expand, setExpand] = useState('');
  const [isExitFromNestedDrawer, setIsExitFromNestedDrawer] = useState(false);
  const [isUserAuthorized, setIsUserAuthorized] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      const role = getUserRole();
      const checkAuthorized = WBCONST.authorizedRoles?.some(
        (rol) => rol.toLowerCase() === role?.toLowerCase()
      );
      setIsUserAuthorized(checkAuthorized);
    }, 1000);
  }, []);

  const menus = useSelector(({ auth }) => auth.modules);
  const filteredMenus = useMemo(
    () =>
      menus &&
      menus.map((module) => ({
        ...module,
        menus: module.menus
          ?.filter((menu) => menu.view === 'Y')
          ?.map((menu) => ({
            ...menu,
            childMenu: menu.childMenu ? menu.childMenu.filter((child) => child.view === 'Y') : [],
          }))
          ?.filter(
            (menu) =>
              (menu.screenPath && menu.screenPath.length > 0 && menu.screenPath.includes('/')) ||
              menu.childMenu.length > 0
          ),
      })),
    [menus]
  );
  // .filter((module) => module.menus.length > 0);

  function translateModules(modules) {
    return modules.map((module) => {
      const moduleNameKey = module.moduleName?.toLowerCase()?.replace(/[\s\W_]+/g, '');
      const translatedModuleName = (moduleNameKey, module.moduleName);
      const translatedMenus = module.menus?.map((menu) => {
        const captionKey = menu.caption.toLowerCase().replace(/[\s\W_]+/g, '');
        const translatedCaption = (captionKey, menu.caption);
        const translatedChildMenu = menu.childMenu
          ? menu.childMenu.map((child) => {
              const childCaptionKey = child.caption.toLowerCase().replace(/[\s\W_]+/g, '');
              return {
                ...child,
                caption: (childCaptionKey, child.caption),
              };
            })
          : undefined;

        return {
          ...menu,
          caption: translatedCaption,
          childMenu: translatedChildMenu,
        };
      });

      return {
        ...module,
        moduleName: translatedModuleName,
        menus: translatedMenus,
      };
    });
  }

  // const translatedmenus = translateModules(menus);
  const translatedmenus = useMemo(
    () => filteredMenus && translateModules(filteredMenus),
    [filteredMenus]
  );
  const { colorShades } = colors;
  const handleMobileHide = () => {
    if (matchMobile) {
      toggleDrawer();
    }
  };
  const drawerClose = () => {
    setOpen(false);
  };
  const drawOpen = () => {
    setOpen(true);
  };

  const [nestedMenuOpen, setNestedMenuOpen] = useState(false);

  const handleSidebarHover = () => {
    setNestedMenuOpen((prev) => !prev);
  };

  const handleSidebarExit = () => {
    setIsExitFromNestedDrawer(true);
  };
  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 109,
        '& .MuiPaper-root': {
          background: colorShades.pureWhite,
          boxShadow: 'none',
          position: open ? 'fixed' : 'inherit',
          height: '100%',
          borderRight: 'none',
        },
      }}
      className="AppSidebar"
      onMouseLeave={drawopen ? drawerClose : handleSidebarExit}
    >
      <Drawer sx={{ borderRight: 'none', height: '100%' }} variant="permanent" open={open}>
        <List
          sx={{
            height: '100%',
            overflow: 'auto',
            backgroundColor: colorShades.pureWhite,
            borderRight: 'none',
            marginTop: open && '36px',
            paddingTop: '0px',
          }}
        >
          {open ? (
            <Box onMouseEnter={handleSidebarHover}>
              {translatedmenus?.map((menu, index) => {
                if (menu?.menus?.length > 0) {
                  return (
                    <React.Fragment key={`nav-item-${index}`}>
                      <SideBarHoverExpanded
                        drawopen={drawopen}
                        onToggle={handleMobileHide}
                        expand={expand}
                        setExpand={setExpand}
                        open={open}
                        menu={menu}
                        setOpen={setOpen}
                        matchMenu={matchMenu}
                        openSubMenuId={openSubMenuId}
                        setOpenSubMenuId={setOpenSubMenuId}
                        nestedMenuOpen={nestedMenuOpen}
                        isExitFromNestedDrawer={isExitFromNestedDrawer}
                        setIsExitFromNestedDrawer={setIsExitFromNestedDrawer}
                      />
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={`nav-item-${index}`}>
                    <SingleLevelItem onToggle={handleMobileHide} open={open} menu={menu} />
                  </React.Fragment>
                );
              })}
            </Box>
          ) : (
            <Box>
              {translatedmenus?.map((menu, index) => {
                if (menu?.menus?.length > 0) {
                  return (
                    <React.Fragment key={`nav-item-${index}`}>
                      <SidebarCollapsed
                        drawopen={drawopen}
                        onToggle={handleMobileHide}
                        expand={expand}
                        setExpand={setExpand}
                        open={open}
                        menu={menu}
                        setOpen={setOpen}
                        matchMenu={matchMenu}
                        openSubMenuId={openSubMenuId}
                        setOpenSubMenuId={setOpenSubMenuId}
                      />
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={`nav-item-${index}`}>
                    <SingleLevelItem onToggle={handleMobileHide} open={open} menu={menu} />
                  </React.Fragment>
                );
              })}
            </Box>
          )}
        </List>
      </Drawer>
    </Box>
  );
}

export default AppSidebar;
